import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BookOpen, Users } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/Dashboard.css";
import { useActiveLearner } from "../../hooks/useActiveLearner";
import { getStudentAuthHeaders } from "../../utils/studentSession";

interface Subject {
  id: string;
  name: string;
}

interface ClassInfo {
  id: string;
  name: string;
}

const MySubjects: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const { activeLearnerId } = useActiveLearner();
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [studentClass, setStudentClass] = useState<ClassInfo | null>(null);
  const [error, setError] = useState<string>("");
// Helper function to get student's class info
  const getStudentClassInfo = useCallback(async () => {
    try {
        const classRes = await axios.get(
          getApiUrl("academics/student/class"),
          { headers: getStudentAuthHeaders(token) }
        );
      return {
        id: classRes.data.id,
        name: classRes.data.name
      };
    } catch (error: any) {
      console.error("Error fetching student class:", error);
      return null;
    }
  }, [token]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!activeLearnerId || !token) {
        setError("Authentication required");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
// Get student's class info and subjects in parallel
        const [classInfo, subjectsRes] = await Promise.all([
          getStudentClassInfo(),
          axios.get(
            getApiUrl("academics/student/subjects"),
            { headers: getStudentAuthHeaders(token) }
          ).catch(() => ({ data: [] }))
        ]);

        if (!classInfo) {
          setError("You are not assigned to any class yet.");
          setLoading(false);
          return;
        }

        setStudentClass(classInfo);
// Get subjects
        const subjectsData = Array.isArray(subjectsRes.data) ? subjectsRes.data : [];
        setSubjects(subjectsData);
      } catch (error: any) {
        console.error("Error fetching subjects:", error);
        setError(
          error.response?.data?.message || 
          "Failed to load subjects. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [activeLearnerId, token, getStudentClassInfo]);

  if (loading) {
    return (
      <div className="dashboard-content w-full">
        <div className="main-content px-6 py-6">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
              <p className="text-gray-600 font-medium">Loading subjects...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content w-full">
        <div className="main-content px-6 py-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <BookOpen className="mx-auto text-yellow-500 mb-4" size={48} />
            <p className="text-yellow-800 text-lg font-semibold mb-2">{error}</p>
            <p className="text-yellow-600 text-sm">
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content w-full">
      <div className="main-content px-6 py-6">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="text-blue-600" size={28} />
            My Subjects
          </h1>
          <p className="text-gray-500 mt-1">
            View all subjects you are enrolled in.
          </p>
          {studentClass && (
            <p className="text-sm text-gray-400 mt-2 flex items-center gap-2">
              <Users size={16} />
              Class: {studentClass.name}
            </p>
          )}
        </header>

        {subjects.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500 text-lg">No subjects found</p>
            <p className="text-gray-400 text-sm mt-2">
              Subjects will appear here once they are assigned to your class.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subjects.map((subject) => (
              <div
                key={subject.id}
                className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <BookOpen className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {subject.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Subject ID: {subject.id}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Card */}
        {subjects.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Subjects</p>
                <p className="text-3xl font-bold text-gray-800">{subjects.length}</p>
              </div>
              <div className="bg-blue-100 p-4 rounded-full">
                <BookOpen className="text-blue-600" size={32} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubjects;











